import React from 'react';
import styled, { css } from 'styled-components/macro';
import delve from 'dlv';

import { MAX_COUNTRY_NAME } from 'config';

import formatNumber from 'helpers/formatNumber';
import getItemColor from './helpers/getItemColor';
import getShortName from 'helpers/getShortName';
import salesTypes from 'maps/sales-types';

import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import { KeyValueList } from 'components/base/KeyValueGroup/KeyValueGroup';
import { Group, Label, Data } from 'components/base/KeyValuePair/KeyValuePair';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import TooltipWrapper from 'components/base/TooltipWrapper';

import CountryFlag from 'components/base/CountryFlag';
import modularScale from 'styles/helpers/modular-scale';
import { ABBREVIATION_THRESHOLD } from 'helpers/abbreviateNumber';

import useMedia from 'hooks/useMedia';
import { ICountryFlagProps } from 'types/shared';
import { SalesTypeValues } from 'maps/sales-types';

const CountryGroup = styled(Group)`
  display: flex;
  flex-direction: column;
  flex-shrink: 1;

  &:first-of-type {
    flex-basis: 0;
  }

  &:first-child {
    margin-bottom: ${({ theme }): string => theme.spacing.xsmall};
  }
`;

interface ICountryDataStylesProps {
  compareMode: any;
  dataColor: any;
  value: number;
}

const CountryData = styled(Data)<ICountryDataStylesProps>`
  display: flex;
  align-self: flex-start;
  align-items: center;
  flex: 1 0 auto;
  font-size: ${modularScale(2)} !important;

  > img,
  > svg {
    margin-right: ${({ theme }): string => theme.spacing.xsmall};
  }

  &:before {
    content: '#' attr(value);
    min-width: 2rem;
    margin-right: 0.5rem;
    font-size: ${modularScale(-1)};
    text-align: right;
    color: ${({ theme }): string => theme.NightBlue};
    display: block; /* Needed for IE11 */
  }

  ${({ compareMode }): any =>
    compareMode === 'entity' &&
    css`
      &:not(:first-child):before {
        content: '';
      }
    `};

  ${({ compareMode, dataColor }): any =>
    compareMode === 'country' &&
    css`
      &:before {
        color: ${dataColor};
      }
    `};
`;

const Wrapper = styled.div`
  overflow: hidden;
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: baseline;
  padding-left: ${({ theme }): string => `calc(2rem + ${theme.spacing.small})`};

  ${CountryGroup}:first-of-type {
    flex-basis: auto;
  }
`;

interface ITopCountryProps {
  pending: boolean;
  fulfilled: boolean;
  data: {
    countryId: string;
    countryName: string;
    countryRank: number;
    entityId: string;
    llw: number;
    lw: number;
    lwtd: number;
    rtd: number;
    rtdOfGlobal: number;
    salesType: SalesTypeValues;
    twtd: number;
    twtdOfGlobal: number;
    wowInProgress: number;
    wowLastCompleted: number;
    lwOfGlobal?: any;
  }[];
  countryFlags: ICountryFlagProps[];
  compareArray: string[];
  compareMode?: string;
  dataColors: string[];
  sortMetric: 'twtd' | 'rtd' | 'lw';
  salesType?: SalesTypeValues;
  isLargeScreen: boolean;
}

type RtdItemType =
  | 'number'
  | 'percentage'
  | 'trend'
  | 'position'
  | 'change'
  | undefined;

interface IComparingEntity {
  compareArrayIndex: number;
  countryCode?: string;
  countryDisplayName?: string;
  countryRank?: number;
  index: number;
  type: string;
}

const TopCountry: React.FC<ITopCountryProps> = (props) => {
  const matchesSmall = useMedia('(min-width: 30em)');
  const matchesMedium = useMedia('(min-width: 60em)');
  const matchesLarge = useMedia('(min-width: 90em)');

  let showShortCountryCode = true;
  if (matchesSmall) showShortCountryCode = false;
  if (matchesMedium) showShortCountryCode = true;
  if (matchesLarge) showShortCountryCode = false;

  const {
    pending,
    fulfilled,
    data = [],
    countryFlags,
    compareArray,
    compareMode,
    dataColors,
    sortMetric,
    salesType,
    isLargeScreen,
  } = props;

  if (pending) {
    return (
      <Wrapper>
        <KPILoader showPrevious showChange />
      </Wrapper>
    );
  }

  /**
   * sortByCompareArrayIndex looks at the current compareMode, and sorts the items
   * in the Territory topic so they match the order of the compareMode instead of.
   * the default sorting order of twtd or rtd.
   * Prerequisite: The items should already have a compareArrayIndex assigned, otherwise this function
   * will not sort at all
   * @param {object} a The first territory object
   * @param {object} b The second territory
   */
  const sortByCompareArrayIndex = (
    a: IComparingEntity,
    b: IComparingEntity
  ): number => {
    // Only sort when in compare mode
    if (compareMode) {
      if (a.compareArrayIndex < b.compareArrayIndex) {
        return -1;
      }
      if (a.compareArrayIndex > b.compareArrayIndex) {
        return 1;
      }
    }
    // Do not change the sorting order
    return 0;
  };

  const countryItems = data
    ?.map(({ countryId, countryName, countryRank }, index) => ({
      type: 'country',
      countryCode: countryId,
      countryDisplayName: countryName,
      index: index,
      countryRank,
      compareArrayIndex: compareArray.findIndex((compareItem) => {
        return countryId === compareItem;
      }),
    }))
    .sort(sortByCompareArrayIndex);

  const rtdItems = data
    ?.map((item, index) => {
      const val = {
        index,
        type: 'number',
        playCount: 0,
        compareArrayIndex: 0,
      };

      val.playCount = item[sortMetric];

      val.compareArrayIndex = compareArray.findIndex((compareItem) => {
        return item.countryId === compareItem;
      });

      return val;
    })
    .sort(sortByCompareArrayIndex);

  const ofGlobalItems = data
    .map((item, index) => {
      const { countryId, rtdOfGlobal, twtdOfGlobal, lwOfGlobal } = item;

      const val = {
        index,
        type: 'percentage',
        globalValue: 0,
        compareArrayIndex: 0,
      };
      if (sortMetric === 'rtd') {
        val.globalValue = rtdOfGlobal;
      } else if (sortMetric === 'twtd') {
        val.globalValue = twtdOfGlobal;
      } else if (sortMetric === 'lw') {
        val.globalValue = lwOfGlobal;
      }

      val.compareArrayIndex = compareArray.findIndex((compareItem) => {
        return countryId === compareItem;
      });
      return val;
    })
    .sort(sortByCompareArrayIndex);

  // Shorten the countries to country codes in certain conditions
  const shouldShowCountryCode = showShortCountryCode;

  return (
    <Wrapper
      data-testid={`${sortMetric}-top-country`}
      data-pw={`${sortMetric}-top-country`}
    >
      {fulfilled && (
        <>
          <KeyValueList>
            <CountryGroup data-pw="top-country-group">
              {countryItems.map((item, index) => {
                const { countryCode, countryRank } = item;

                const countryDisplayName = delve(
                  item,
                  'countryDisplayName',
                  '–'
                );

                const color = getItemColor(item, compareMode, dataColors);

                const flag = countryFlags.find(
                  (flagItem: ICountryFlagProps): boolean => {
                    return countryCode === flagItem.id;
                  }
                );
                const countryName = shouldShowCountryCode
                  ? countryCode
                  : getShortName(countryDisplayName);

                return (
                  <CountryData
                    key={`tctw-${index}`}
                    dataColor={color}
                    compareMode={compareMode}
                    value={countryRank}
                    data-pw={`territory-kpis-${sortMetric}-top-country-entity-${index}`}
                    data-country={countryCode}
                  >
                    {flag && (
                      <CountryFlag
                        {...flag}
                        small={!isLargeScreen}
                        data-testid="flag"
                      />
                    )}
                    <TooltipWrapper
                      condition={countryName?.length > MAX_COUNTRY_NAME}
                      tooltipContent={countryDisplayName}
                    >
                      <span>{countryName}</span>
                    </TooltipWrapper>
                  </CountryData>
                );
              })}
            </CountryGroup>

            <StatsWrapper>
              <CountryGroup data-pw="top-country-group">
                <Label>{salesTypes[salesType as SalesTypeValues].label}</Label>
                {rtdItems.map((item, index) => {
                  const { playCount, type } = item;

                  const fullValue = formatNumber(
                    playCount,
                    type as RtdItemType,
                    false
                  );
                  const displayValue = formatNumber(
                    playCount,
                    type as RtdItemType,
                    true
                  );

                  const color = getItemColor(item, compareMode, dataColors);

                  return (
                    <Data
                      key={`${salesType}-${index}`}
                      dataColor={color}
                      isBlack={!compareMode}
                    >
                      <TippyToolTip
                        enabled={Math.abs(playCount) >= ABBREVIATION_THRESHOLD}
                        tooltipContent={fullValue}
                        canBeCopied
                      >
                        {displayValue}
                      </TippyToolTip>
                    </Data>
                  );
                })}
              </CountryGroup>

              <CountryGroup data-pw="top-country-group">
                <Label>Of global</Label>
                {ofGlobalItems.map((item, index) => {
                  const { globalValue, type } = item;

                  const color = getItemColor(item, compareMode, dataColors);

                  return (
                    <Data key={`of-global-${index}`} dataColor={color}>
                      {formatNumber(globalValue, type as RtdItemType, true)}
                    </Data>
                  );
                })}
              </CountryGroup>
            </StatsWrapper>
          </KeyValueList>
        </>
      )}
    </Wrapper>
  );
};

export default TopCountry;
